<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue'

import FormInput from '@tix/ds/components/atoms/FormInput.vue'
import type { FormInputExposed } from '@tix/ds/components/atoms/form-input/types.ts'

import { isPlaceholderEmail } from '@/helpers/placeholderEmail'

import type { IdentityFormValidateResult } from '@/components/IdentityForm.vue'

defineProps<{ disabled?: boolean }>()

const email = ref('')
const emailConfirm = ref('')

const emailRef = useTemplateRef<FormInputExposed>('emailInput')
const emailConfirmRef = useTemplateRef<FormInputExposed>('emailConfirmInput')

const PLACEHOLDER_EMAIL_ERROR = 'Please enter a valid email address.'
const CONFIRM_MISMATCH_ERROR = 'Email addresses must match.'

function emailFieldError(): string | undefined {
  if (!email.value) return undefined
  return isPlaceholderEmail(email.value) ? PLACEHOLDER_EMAIL_ERROR : undefined
}

function confirmFieldError(): string | undefined {
  if (!emailConfirm.value) return undefined
  return email.value.toLowerCase() === emailConfirm.value.toLowerCase() ? undefined : CONFIRM_MISMATCH_ERROR
}

const finalFormData = computed(() => ({ email: email.value }))

function validate(): IdentityFormValidateResult {
  let countOfErrors = 0

  if (emailRef.value?.validateAndReturnError() || emailFieldError()) countOfErrors++
  if (emailConfirmRef.value?.validateAndReturnError() || confirmFieldError()) countOfErrors++

  if (countOfErrors === 0) return {}

  return {
    error: countOfErrors === 1 ? '1 field needs your attention.' : `${countOfErrors} fields need your attention.`,
  }
}

defineExpose({ finalFormData, validate })
</script>

<template>
  <section class="member-email-form">
    <h2 class="member-email-form-heading">Your information</h2>

    <div class="member-email-form-fields">
      <FormInput
        id="email"
        ref="emailInput"
        v-model="email"
        autocomplete="email"
        :disabled="disabled"
        :error="emailFieldError()"
        label="Email address"
        maxlength="254"
        required
        type="email"
      />
      <FormInput
        id="email_confirm"
        ref="emailConfirmInput"
        v-model="emailConfirm"
        autocomplete="email"
        :disabled="disabled"
        :error="confirmFieldError()"
        label="Confirm email address"
        maxlength="254"
        required
        type="email"
      />
    </div>
  </section>
</template>

<style scoped>
.member-email-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);

  .member-email-form-heading {
    margin: 0;
    font-size: var(--font-size-h2);
    color: var(--text-primary);
  }

  .member-email-form-fields {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }
}
</style>
